import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityMonitoringService } from 'app/entities/monitoring/entity-monitoring.service';
import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';

/** Set to true to log debug info to console; set false before committing */
const DEBUG_MATCH_INDICATORS = true;
const debugLog = (...args: any[]) => { if (DEBUG_MATCH_INDICATORS) { console.log('[MatchIndicators]', ...args); } };

/**
 * Compact indicators showing whether the entity has sanction, PEP, or bad press matches.
 * Displayed in the entity header tasks bar. Each indicator appears only when there is a match.
 */
@Component({
  selector: 'monitoring-match-indicators',
  templateUrl: './monitoring-match-indicators.component.html',
  styleUrls: ['./monitoring-match-indicators.component.css']
})
export class MonitoringMatchIndicatorsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entityIdDetails: EntityIdDetails;

  hasSanctionMatch = false;
  hasPepMatch = false;
  hasBadPressMatch = false;
  loading = false;

  private sanctionPepResolutions: ApplicationValue[];
  private badPressResolutions: ApplicationValue[];
  private matchDataChangedSubscription: any;

  constructor(
    private entityMonitoringService: EntityMonitoringService,
    private warmUpService: WarmUpService
  ) {}

  ngOnInit() {
    debugLog('ngOnInit, entityIdDetails=', this.entityIdDetails);
    this.matchDataChangedSubscription = this.entityMonitoringService.matchDataChanged.subscribe(() => {
      debugLog('matchDataChanged fired', { entityId: this.entityIdDetails && this.entityIdDetails.entityId });
      if (this.entityIdDetails && this.entityIdDetails.entityId) {
        // Use loadResolutionsAndMatchIndicators to ensure resolutions are ready before counting
        // positives (avoids race where matchDataChanged fires before initial resolution load completes)
        this.loadResolutionsAndMatchIndicators();
      }
    });
  }

  ngOnDestroy() {
    if (this.matchDataChangedSubscription) {
      this.matchDataChangedSubscription.unsubscribe();
    }
  }

  ngOnChanges() {
    debugLog('ngOnChanges', this.entityIdDetails);
    if (!this.entityIdDetails || !this.entityIdDetails.entityId) {
      this.hasSanctionMatch = false;
      this.hasPepMatch = false;
      this.hasBadPressMatch = false;
      return;
    }
    this.loadResolutionsAndMatchIndicators();
  }

  private loadResolutionsAndMatchIndicators() {
    this.loading = true;

    let sanctionReady = false;
    let badPressReady = false;
    const maybeLoad = () => {
      if (sanctionReady && badPressReady) {
        debugLog('resolutions loaded', {
          sanctionPepResolutions: this.sanctionPepResolutions,
          badPressResolutions: this.badPressResolutions
        });
        this.loadMatchIndicators();
      }
    };

    this.warmUpService.getApplicationValueList(ApplicationTypeConstants.SANCTION_MATCH_RESOLUTION, (values: any) => {
      this.sanctionPepResolutions = Array.isArray(values) ? values : [];
      sanctionReady = true;
      maybeLoad();
    });
    this.warmUpService.getApplicationValueList(ApplicationTypeConstants.PANAMA_PAPERS_MATCH_RESOLUTION, (values: any) => {
      this.badPressResolutions = Array.isArray(values) ? values : [];
      badPressReady = true;
      maybeLoad();
    });
  }

  private loadMatchIndicators() {
    const entityId = this.entityIdDetails.entityId;
    const isPerson = this.entityIdDetails.isPerson;

    const requests: Array<Observable<any>> = [
      this.entityMonitoringService.getSanctionHistory(entityId),
      this.entityMonitoringService.getBadPressHistory(entityId)
    ];
    if (isPerson) {
      requests.push(this.entityMonitoringService.getPepHistory(entityId));
    }

    Observable.forkJoin(requests).subscribe(responses => {
      const toList = (r: any) =>
        (r && (r as any).suggestionList) || (Array.isArray(r) ? r : []);
      const sanctionList = toList(responses[0]);
      const badPressList = toList(responses[1]);
      const pepList = isPerson ? toList(responses[2]) : [];

      const sanctionPositive = this.countPositiveMatches('Sanction', sanctionList);
      const badPressPositive = this.countPositiveMatches('Badpress', badPressList);
      const pepPositive = isPerson ? this.countPositiveMatches('Pep', pepList) : 0;

      debugLog('history loaded', {
        entityId,
        sanctionListLen: sanctionList.length,
        sanctionList,
        sanctionPositive,
        badPressListLen: badPressList.length,
        badPressPositive,
        pepListLen: pepList.length,
        pepPositive
      });

      this.hasSanctionMatch = sanctionPositive > 0;
      this.hasBadPressMatch = badPressPositive > 0;
      this.hasPepMatch = isPerson && pepPositive > 0;

      this.loading = false;
    }, (err) => {
      debugLog('loadMatchIndicators error', err);
      this.loading = false;
    });
  }

  private countPositiveMatches(type: 'Sanction' | 'Pep' | 'Badpress', list: any[]): number {
    if (!list || !Array.isArray(list)) {
      return 0;
    }
    return list.filter(r => this.isPositiveResolution(type, r && r.matchResolution)).length;
  }

  private isPositiveResolution(type: 'Sanction' | 'Pep' | 'Badpress', id: any): boolean {
    if (id === null || id === undefined) {
      return false;
    }
    const raw = type === 'Badpress' ? this.badPressResolutions : this.sanctionPepResolutions;
    const list = Array.isArray(raw) ? raw : [];
    if (list.length === 0) {
      debugLog('isPositiveResolution: no resolution list for', type);
      return false;
    }
    const match = list.find((v: ApplicationValue) => v.id == id || String(v.id) === String(id));
    const label = match ? (match.name || (match as any).value || '') : '';
    const result = /true/i.test(label);
    if (DEBUG_MATCH_INDICATORS && type === 'Sanction' && (match || id != null)) {
      debugLog('isPositiveResolution Sanction', { id, label, result, listIds: list.map((v: any) => v.id) });
    }
    return result;
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/monitoring/monitoring-match-indicators/monitoring-match-indicators.component.ts